import {
  Credit,
  UrlTemplateImageryProvider,
  WebMercatorTilingScheme,
} from "@cesium/engine";
import defined from "../../Core/defined.js";
import DeveloperError from "../../Core/DeveloperError.js";
import Resource from "../../Core/Resource.js";
import Utils from "../../../../widgets/Source/Ditap/basic/Utils.js";

/**
 * V World 지도 이미지 제공 클래스.</br>
 * Class providing VWorld imagery in DiTAP.
 *
 * @alias VWorldImageryProvider
 * @constructor
 * 
 * @param {object} options 다음과 같은 속성을 가진 객체.</br> Object with the following options:
 * @param {string} options.apikey VWorld에서 발급 받은 apikey. 
 * </br>(API 키 발급처: https://www.vworld.kr/dev/v4dv_apikey_s001.do)
 * @param {string} options.mapType VWorld 지도 유형.
  </br>&nbsp&nbsp&nbsp1. "base"- 일반지도
  </br>&nbsp&nbsp&nbsp2. "gray"- 백지도
  </br>&nbsp&nbsp&nbsp3. "midnight"- Midnight 지도
  </br>&nbsp&nbsp&nbsp4. "hybrid"- 하이브리드 지도
  </br>&nbsp&nbsp&nbsp5. "satellite"- 영상지도
 * </br>The type of VWorld map. 
 * </br>(ex: "base", "gray", "midnight", "hybrid", "satellite")
 *
 * @example
  // DitapViewer 생성
  const viewer = new Ditap.DitapViewer("ditapContainer", {
    baseLayerPicker: true,
  });

  // DitapBaseLayerPicker 인스턴스 생성
  const ditapBaseLayerPicker = viewer.ditapBaseLayerPicker;

  const vWorldImageryProvider = new Ditap.ProviderViewModel({
    name: "VWorld Korea Map",
    iconUrl: Utils.getUrlFromBase64(
      Ditap.ConstantUrl.VWORLD_MAP_IMG_URL
    ),
    tooltip: "VWorld Korea Map",
    category: "Other",
    creationFunction: function () {
      return new Ditap.VWorldImageryProvider({
        // 발급 받은 apiKey 추가 (필수)
        apiKey: apiKey,

        // 맵 타입 추가 (필수)
        mapType: "base",
      });
    },
  });
    
  ditapBaseLayerPicker.viewModel.selectedImagery = vWorldImageryProvider;
  
  // * 하이브리드("hybrid") 지도는 단독으로 사용하는 지도가 아닌 다른 지도(위성영상 등의 정보 표기가 없는 영상지도)를 아래에 겹쳐 정보를 보여주는 목적의 지도입니다.
  // 하이브리드 지도 사용시 두 개 이상의 지도를 배열로 반환하여 사용합니다.
  // const vWorldImageryProvider = new Ditap.ProviderViewModel({
  //   name: "VWorld Korea Map",
  //   iconUrl: Utils.getUrlFromBase64(
  //     Ditap.ConstantUrl.VWORLD_MAP_IMG_URL
  //   ),
  //   tooltip: "VWorld Korea Map",
  //   category: "Other",
  //   creationFunction: function () {
  //     const base =  new Ditap.VWorldImageryProvider({
  //       apiKey: "ABB43517-2006-318D-A8DB-CAC4A4FBB5B5",
  //       mapType: "satellite",
  //     });
  //     const hyb =  new Ditap.VWorldImageryProvider({
  //       apiKey: "ABB43517-2006-318D-A8DB-CAC4A4FBB5B5",
  //       mapType: "hybrid",
  //     });

  //     return [base, hyb];
  //   },
  // }); 
 
 */
class VWorldImageryProvider extends UrlTemplateImageryProvider {
  constructor(options) {
    if (!defined(options.apiKey)) {
      throw new DeveloperError("options.apiKey is required.");
    }

    if (!defined(options.mapType) || !isAvaillableMapType(options.mapType)) {
      throw new DeveloperError("options.mapType is required.");
    } else {
      options.mapType = getMapType(options.mapType);
      options.imgType = getImgType(options.mapType);
    }

    function isAvaillableMapType(mapType) {
      mapType = String(mapType).trim().toLowerCase();
      let isAvailableMapType = false;
      if (
        mapType === "base" ||
        mapType === "gray" ||
        mapType === "midnight" ||
        mapType === "hybrid" ||
        mapType === "satellite"
      ) {
        isAvailableMapType = true;
      }
      return isAvailableMapType;
    }

    function getMapType(mapType) {
      mapType = String(mapType).trim().toLowerCase();
      let availableMapType;
      if (mapType === "base") {
        availableMapType = "Base";
      } else if (mapType === "gray") {
        availableMapType = "gray";
      } else if (mapType === "midnight") {
        availableMapType = "midnight";
      } else if (mapType === "hybrid") {
        availableMapType = "Hybrid";
      } else if (mapType === "satellite") {
        availableMapType = "Satellite";
      }
      return availableMapType;
    }

    function getImgType(mapType) {
      mapType = String(mapType).trim().toLowerCase();
      let imgType;
      if (
        mapType === "base" ||
        mapType === "midnight" ||
        mapType === "gray" ||
        mapType === "hybrid"
      ) {
        imgType = "png";
      } else if (mapType === "satellite") {
        imgType = "jpeg";
      }
      return imgType;
    }

    /*
    유효값
    Base : 6~19
    gray : 6~18
    midnight : 6~18
    Hybrid : 6~19
    Satellite : 6~19
    */
    const protocol = Utils.getProtocol();
    const customResource = new Resource({
      url: `${protocol}//api.vworld.kr/req/wmts/1.0.0/${options.apiKey}/${options.mapType}/{z}/{y}/{x}.${options.imgType}`,
    });

    const resource = customResource;
    options.url = resource;
    options.tilingScheme = new WebMercatorTilingScheme();
    options.tileWidth = 256;
    options.tileHeight = 256;
    options.minimumLevel = 0;
    options.rectangle = options.tilingScheme.rectangle;
    options.hasAlphaChannel = true;

    options.credit = new Credit("© Digital Twin National Land, CC-BY-ND");

    super(options);
  }

  getTileCredits() {
    return;
  }

  pickFeatures() {
    return;
  }

  // requestImage() {
  //   return;
  // }
}
export default VWorldImageryProvider;
